import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { GraduationCap, BookOpen, Calendar, ChartLine, CaretDown, CaretRight } from "@phosphor-icons/react";

const KIND_LABEL = {
  homework: "Devoir", exercise: "Exercice", quiz: "Quiz", test: "Test",
  exam: "Examen", project: "Projet", presentation: "Présentation",
  participation: "Participation", practical: "TP", custom: "Autre",
  essay: "Rédaction", composition: "Composition", devoir: "Devoir",
};

const gradeColor = (n) => {
  if (n == null) return "text-muted-foreground";
  if (n >= 16) return "text-secondary font-bold";
  if (n >= 12) return "text-primary font-semibold";
  if (n >= 10) return "text-foreground";
  return "text-destructive font-semibold";
};

export default function Gradebook({ studentIdProp }) {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(studentIdProp || "");
  const [gb, setGb] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  // Permission to trigger PDF / email actions (admin, director, teacher, etc.).
  const canGenerate = ["super_admin","owner","admin","director","study_director","teacher","secretary"].includes(user?.role);
  const canEmail = ["super_admin","owner","admin","director","study_director","teacher"].includes(user?.role);
  const activeLang = (typeof window !== "undefined" && (localStorage.getItem("kosyam_lang") || "fr").startsWith("en")) ? "en" : "fr";

  const downloadPeriod = async (lang, period) => {
    if (!studentId) return;
    const stu = students.find(s => s.id === studentId) || {};
    try {
      const { downloadFile } = await import("@/lib/api");
      await downloadFile(`/bulletins/${studentId}/pdf?period=${encodeURIComponent(period)}&lang=${lang}`,
        `bulletin_${stu.matricule || studentId}_${period}_${lang}.pdf`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadAnnual = async (lang) => {
    if (!studentId) return;
    const stu = students.find(s => s.id === studentId) || {};
    try {
      const { downloadFile } = await import("@/lib/api");
      await downloadFile(`/bulletins/${studentId}/pdf-annual?lang=${lang}`,
        `bulletin_annuel_${stu.matricule || studentId}_${lang}.pdf`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const emailAnnual = async () => {
    if (!studentId) return;
    const stu = students.find(s => s.id === studentId) || {};
    if (!confirm(`Envoyer le bulletin annuel de ${stu.first_name || "cet élève"} aux parents par email ?`)) return;
    try {
      const r = await api.post(`/bulletins/${studentId}/email-annual?lang=${activeLang}`);
      toast.success(`Bulletin envoyé (${r.data.emails_sent}/${r.data.attempted} email${r.data.attempted > 1 ? "s" : ""}).`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (studentIdProp) { setStudentId(studentIdProp); return; }
    if (["parent","student"].includes(user?.role)) return; // switcher not needed
    api.get("/students").then(r => {
      setStudents(r.data);
      if (r.data.length && !studentId) setStudentId(r.data[0].id);
    }).catch(e => toast.error(formatApiError(e)));
    // eslint-disable-next-line
  }, [studentIdProp]);

  useEffect(() => {
    if (!studentId && user?.role === "student") {
      // student: hit their own profile — the API resolves it from token
      api.get("/students").then(r => { if (r.data[0]) setStudentId(r.data[0].id); });
      return;
    }
    if (!studentId) return;
    setLoading(true);
    api.get("/grades/gradebook", { params: { student_id: studentId } })
      .then(r => setGb(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [studentId, user?.role]);

  const toggle = (k) => setExpanded(e => ({ ...e, [k]: !e[k] }));

  if (!studentIdProp && !["parent","student"].includes(user?.role)) {
    // header with student picker
  }

  return (
    <div className={studentIdProp ? "" : "p-6 lg:p-10 max-w-6xl mx-auto"}>
      {!studentIdProp && (
        <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Suivi académique</div>
            <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <GraduationCap size={32} weight="duotone" className="text-primary" />
              Notes & Bulletins
            </h1>
          </div>
          {!["parent","student"].includes(user?.role) && students.length > 0 && (
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger className="w-72" data-testid="gradebook-student-picker"><SelectValue placeholder="Choisir un élève" /></SelectTrigger>
              <SelectContent>
                {students.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.last_name} {s.first_name} · {s.matricule}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </header>
      )}

      {loading ? (
        <Card className="p-10 text-center text-sm text-muted-foreground">Chargement…</Card>
      ) : !gb || gb.empty ? (
        <Card className="p-10 text-center text-sm text-muted-foreground">Aucune note publiée pour l'instant.</Card>
      ) : (
        <>
          {/* PDF & email actions — only for admin/director/teacher on the standalone route */}
          {!studentIdProp && canGenerate && studentId && (
            <Card className="mb-4 p-4 flex flex-wrap items-center gap-2 border-primary/30 bg-primary/5" data-testid="bulletin-actions">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold mr-2 flex items-center gap-1.5">
                <GraduationCap size={14} weight="duotone" /> Générer le bulletin
              </div>
              {gb?.periods?.map(p => (
                <Button key={p} size="sm" variant="outline" onClick={() => downloadPeriod(activeLang, p)} data-testid={`btn-gradebook-download-${p}`}>
                  {p} ({activeLang.toUpperCase()})
                </Button>
              ))}
              <Button size="sm" onClick={() => downloadAnnual(activeLang)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="btn-gradebook-download-annual">
                Bulletin annuel {activeLang.toUpperCase()}
              </Button>
              {canEmail && (
                <Button size="sm" variant="outline" onClick={emailAnnual} className="border-accent/50 text-accent hover:bg-accent/10" data-testid="btn-gradebook-email-annual">
                  📧 Envoyer aux parents
                </Button>
              )}
            </Card>
          )}
          {/* KPI header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="p-4" data-testid="kpi-overall"><div className="text-[10px] uppercase tracking-wider text-primary font-semibold">Moyenne annuelle</div>
              <div className={`font-heading text-3xl font-bold mt-1 tabular-nums ${gradeColor(gb.annual.overall_average)}`}>{gb.annual.overall_average ?? "—"}<span className="text-sm text-muted-foreground font-normal">/20</span></div>
            </Card>
            {gb.periods.map(p => {
              const per = gb.by_period.find(x => x.period === p);
              const avg = per?.period_average;
              return (
                <Card key={p} className="p-4" data-testid={`kpi-${p}`}><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Moy. {p}</div>
                  <div className={`font-heading text-3xl font-bold mt-1 tabular-nums ${gradeColor(avg)}`}>{avg ?? "—"}<span className="text-sm text-muted-foreground font-normal">/20</span></div>
                </Card>
              );
            })}
          </div>

          <Tabs defaultValue="by_subject">
            <TabsList data-testid="gradebook-tabs">
              <TabsTrigger value="by_subject" data-testid="tab-by-subject"><BookOpen size={14} weight="duotone" className="mr-1.5" /> Par matière</TabsTrigger>
              <TabsTrigger value="by_period" data-testid="tab-by-period"><Calendar size={14} weight="duotone" className="mr-1.5" /> Par période</TabsTrigger>
              <TabsTrigger value="annual" data-testid="tab-annual"><ChartLine size={14} weight="duotone" className="mr-1.5" /> Vue annuelle</TabsTrigger>
            </TabsList>

            {/* -------- BY SUBJECT -------- */}
            <TabsContent value="by_subject" className="mt-4 space-y-3">
              {gb.by_subject.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">Aucune matière évaluée</Card>}
              {gb.by_subject.map(s => {
                const isOpen = expanded[`s_${s.subject_id}`] !== false;
                return (
                  <Card key={s.subject_id} data-testid={`subject-${s.subject_id}`}>
                    <button onClick={() => toggle(`s_${s.subject_id}`)} className="w-full text-left p-4 flex items-center justify-between hover:bg-muted/40 transition">
                      <div className="flex items-center gap-2">
                        {isOpen ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                        <div>
                          <div className="font-heading font-semibold">{s.subject_name} <span className="text-xs text-muted-foreground font-normal">· coef {s.coefficient} · {s.evaluations.length} éval.</span></div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {gb.periods.map(p => (
                          <span key={p} className="text-xs" data-testid={`subject-${s.subject_id}-per-${p}`}>
                            <span className="text-muted-foreground">{p}: </span>
                            <span className={`font-mono ${gradeColor(s.period_averages[p])}`}>{s.period_averages[p] ?? "—"}</span>
                          </span>
                        ))}
                        <Badge className={`bg-primary text-primary-foreground text-sm ${gradeColor(s.annual_average)}`} data-testid={`subject-${s.subject_id}-annual`}>
                          {s.annual_average ?? "—"}<span className="opacity-70">/20</span>
                        </Badge>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-border overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                              <th className="px-3 py-2 text-left">Date</th>
                              <th className="px-3 py-2 text-left">Évaluation</th>
                              <th className="px-3 py-2 text-left">Type</th>
                              <th className="px-3 py-2 text-center">Période</th>
                              <th className="px-3 py-2 text-center">Poids</th>
                              <th className="px-3 py-2 text-right">Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {s.evaluations.map((e, i) => (
                              <tr key={e.grade_id} className={`border-t border-border ${i % 2 ? "bg-muted/20" : ""}`}>
                                <td className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">{e.date ? new Date(e.date).toLocaleDateString("fr-FR") : "—"}</td>
                                <td className="px-3 py-2 font-medium">{e.title}</td>
                                <td className="px-3 py-2 text-xs">{KIND_LABEL[e.kind] || e.kind}</td>
                                <td className="px-3 py-2 text-center text-xs">{e.period}</td>
                                <td className="px-3 py-2 text-center text-xs tabular-nums">{e.weight}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${gradeColor(e.normalized)}`}>
                                  {e.score}<span className="text-muted-foreground text-xs">/{e.max_score}</span>
                                  {e.max_score !== 20 && <span className="text-[10px] text-muted-foreground ml-1">({e.normalized}/20)</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                );
              })}
            </TabsContent>

            {/* -------- BY PERIOD -------- */}
            <TabsContent value="by_period" className="mt-4 space-y-4">
              {gb.by_period.map(per => (
                <Card key={per.period} className="overflow-hidden" data-testid={`period-${per.period}`}>
                  <div className="p-4 bg-muted/30 flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-secondary font-semibold">{per.period}</div>
                      <div className="text-xs text-muted-foreground">{per.subjects.length} matière(s)</div>
                    </div>
                    <Badge className={`bg-primary text-primary-foreground text-base ${gradeColor(per.period_average)}`}>
                      Moy. générale {per.period_average ?? "—"}/20
                    </Badge>
                  </div>
                  {per.subjects.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">Aucune note pour cette période</div>
                  ) : (
                    <div className="divide-y divide-border">
                      {per.subjects.map(s => (
                        <div key={s.subject_id} className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-medium">{s.subject_name} <span className="text-xs text-muted-foreground">coef {s.coefficient}</span></div>
                            <Badge variant="outline" className={gradeColor(s.average)}>{s.average}/20</Badge>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {s.evaluations.map(e => (
                              <div key={e.grade_id} className="text-[11px] bg-muted rounded px-2 py-1 border border-border">
                                <span className="font-medium">{e.title}</span>
                                <span className="text-muted-foreground"> · {KIND_LABEL[e.kind] || e.kind} · </span>
                                <span className={`font-mono ${gradeColor(e.normalized)}`}>{e.score}/{e.max_score}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </TabsContent>

            {/* -------- ANNUAL MATRIX -------- */}
            <TabsContent value="annual" className="mt-4">
              <Card className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">Matière</th>
                      <th className="px-3 py-2 text-center text-xs uppercase tracking-wider text-muted-foreground">Coef</th>
                      <th className="px-3 py-2 text-center text-xs uppercase tracking-wider text-muted-foreground">Évals</th>
                      {gb.annual.periods.map(p => (
                        <th key={p} className="px-3 py-2 text-center text-xs uppercase tracking-wider text-muted-foreground">{p}</th>
                      ))}
                      <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-primary font-bold">Annuelle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gb.annual.rows.map((r, i) => (
                      <tr key={r.subject_id} className={`border-b border-border ${i % 2 ? "bg-muted/20" : ""}`} data-testid={`annual-row-${r.subject_id}`}>
                        <td className="px-3 py-2 font-medium">{r.subject_name}</td>
                        <td className="px-3 py-2 text-center text-xs tabular-nums">{r.coefficient}</td>
                        <td className="px-3 py-2 text-center text-xs text-muted-foreground">{r.eval_count}</td>
                        {gb.annual.periods.map(p => (
                          <td key={p} className={`px-3 py-2 text-center tabular-nums ${gradeColor(r.period_averages[p])}`}>
                            {r.period_averages[p] ?? "—"}
                          </td>
                        ))}
                        <td className={`px-3 py-2 text-right font-bold tabular-nums ${gradeColor(r.annual_average)}`}>
                          {r.annual_average ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-primary/40 bg-primary/5">
                      <td className="px-3 py-2 font-bold uppercase tracking-wider text-xs" colSpan={3 + gb.annual.periods.length}>Moyenne générale annuelle</td>
                      <td className={`px-3 py-2 text-right font-heading text-lg font-bold tabular-nums ${gradeColor(gb.annual.overall_average)}`}>
                        {gb.annual.overall_average ?? "—"}<span className="text-sm">/20</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
